// src/modules/socialhive/mobile/MobileChannelDetails.jsx
import React, { useContext, useEffect, useMemo, useState } from "react";
import "./MobileChannelDetails.css";
import Services from "../../services/Services";
import { useLanguage } from "../../LanguageProvider";
import { GeneralContext } from "../../GeneralProvider";
import previewPlaceholder from "../../assets/images/preview-bg.webp";
import { useNavigate } from "react-router-dom";

export default function MobileChannelDetails({
    channelID,
    token,
    onBack,
    onRequestLogin,
    onEventClick, // opsiyonel
}) {
    const navigate = useNavigate();
    const { getTranslation } = useLanguage();
    const { myChannels, handleJoinChannel, getMyChannels } = useContext(GeneralContext) || {};
    const [loading, setLoading] = useState(false);
    const [channel, setChannel] = useState(null);
    const [error, setError] = useState("");

    const [events, setEvents] = useState([]);
    const [eventsLoading, setEventsLoading] = useState(false);
    const [eventsError, setEventsError] = useState("");

    const toId = (v) => (v == null ? null : String(v));

    const getLoginToken = () => {
        const remember = localStorage.getItem("rememberMe") === "true";
        const localToken = localStorage.getItem("token");
        const sessionToken = sessionStorage.getItem("token");

        if (remember && localToken) return localToken;
        if (!remember && sessionToken) return sessionToken;
        return null;
    };

    const getEffectiveToken = () => {
        const loginToken = getLoginToken();
        return loginToken || token;
    };

    const isAuth = !!getLoginToken();

    // Kanal detayı
    useEffect(() => {
        if (!channelID) return;

        const t = getEffectiveToken();
        if (!t || typeof Services.SocialHiveGetChannelDetails !== "function") return;
        getMyChannels()
        setLoading(true);
        setError("");

        Services.SocialHiveGetChannelDetails(t, channelID)
            .then((res) => {
                if (res?.data?.status && res?.data?.result) {
                    setChannel(res.data.result);
                } else {
                    setChannel(null);
                }
            })
            .catch(() => {
                setError(getTranslation("somethingWentWrong") || "Something went wrong");
                setChannel(null);
            })
            .finally(() => setLoading(false));
    }, [channelID, token, getTranslation]);

    // Kanal eventleri
    useEffect(() => {
        if (!channelID) return;

        const t = getEffectiveToken();
        if (!t || typeof Services.SocialHiveGetEvents !== "function") return;

        setEventsLoading(true);
        setEventsError("");

        Services.SocialHiveGetEvents(t, channelID)
            .then((res) => {
                if (res?.data?.status && Array.isArray(res.data.result)) {
                    setEvents(res.data.result);
                } else {
                    setEvents([]);
                }
            })
            .catch(() => {
                setEventsError(getTranslation("somethingWentWrong") || "Something went wrong");
                setEvents([]);
            })
            .finally(() => setEventsLoading(false));
    }, [channelID, token, getTranslation]);

    const title = channel?.title || "";
    const description = channel?.description || "";
    const banner = channel?.topBanner || "";
    const hasBanner = !!banner;
    const rawUserCount = channel?.channelUserCount;

    const userCount = useMemo(() => {
        const n = Number(rawUserCount);
        return Number.isFinite(n) && n > 0 ? n : null;
    }, [rawUserCount]);

    const descParagraphs = useMemo(() => {
        if (!description) return [];
        return description
            .split(/\n+/)
            .map((p) => p.trim())
            .filter(Boolean);
    }, [description]);

    //TODO bakılacak
    const isInChannel = useMemo(() => {
        if (!channel?.channelID || !isAuth) return false;
        const targetId = toId(channel.channelID);
        const list = Array.isArray(myChannels) ? myChannels : [];
        console.log(list, "my channels")
        return list.some((ch) => {
            const cid = toId(ch?.channelID ?? ch?.id ?? null);
            return cid === targetId;
        });
    }, [myChannels, channel?.channelID, isAuth]);

    const handleJoinClick = () => {
        if (!channel?.channelID) return;

        if (!isAuth) {
            if (typeof onRequestLogin === "function") {
                onRequestLogin();
            }
            return;
        }

        if (typeof handleJoinChannel === "function") {
            handleJoinChannel(channel.channelID);
        }
    };

    const handleEventCardClick = (ev) => {
        if (!ev) return;

        // Eğer parent özel davranış vermişse onu çalıştır
        if (typeof onEventClick === "function") {
            onEventClick(ev);
            return;
        }

        // Default davranış: eventLink varsa onu aç, yoksa url üzerinden route dene
        navigate(`/event/${ev.eventID}/details`);
        onBack()
    };

    if (loading) {
        return (
            <div className="mch-page">
                <div className="mch-loading">
                    {getTranslation("loading") || "Loading..."}
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="mch-page">
                {onBack && (
                    <button type="button" className="mch-back-btn" onClick={onBack}>
                        {getTranslation("back") || "Back"}
                    </button>
                )}
                <div className="mch-error">{error}</div>
            </div>
        );
    }

    if (!channel) {
        return (
            <div className="mch-page">
                {onBack && (
                    <button type="button" className="mch-back-btn" onClick={onBack}>
                        {getTranslation("back") || "Back"}
                    </button>
                )}
                <div className="mch-empty">
                    {getTranslation("channelInfoNotAvailable") ||
                        "Channel information is not available."}
                </div>
            </div>
        );
    }

    return (
        <div className="mch-page">
            {onBack && (
                <button type="button" className="mch-back-btn" onClick={onBack}>
                    {getTranslation("back") || "Back"}
                </button>
            )}

            {/* BANNER (placeholder destekli) */}
            <div className="mch-banner-wrap">
                <div
                    className={`wa-thumb mch-banner-thumb ${!hasBanner ? "wa-thumb--placeholder" : ""
                        }`}
                >
                    <img
                        src={hasBanner ? banner : previewPlaceholder}
                        alt={title || "Channel banner"}
                        className="mch-banner"
                        onError={(e) => {
                            if (e.currentTarget.src !== previewPlaceholder) {
                                e.currentTarget.src = previewPlaceholder;
                            }
                        }}
                    />
                    {!hasBanner && !!title && (
                        <div className="wa-thumb__placeholder-label wa-thumb__placeholder-label--on-image mch-banner-title-overlay">
                            {title}
                        </div>
                    )}
                </div>
            </div>

            {/* HEADER: title + participant */}
            <div className="mch-header">
                <div className="mch-header-texts">
                    <span className="mch-title">{title}</span>

                    {userCount != null && (
                        <div className="mch-participant">
                            <strong>{userCount}</strong>{" "}
                            {getTranslation("participant") || "Participants"}
                        </div>
                    )}
                </div>
            </div>

            {/* JOIN CHANNEL BUTTON */}
            <div className="mch-join-wrapper">
                <button
                    type="button"
                    className="mle-btn mle-btn-primary mch-join-btn"
                    style={{
                        borderRadius: "6px",
                        height: "47px",
                        fontSize: "18px",
                        width: "100%",
                    }}
                    disabled={isInChannel}
                    onClick={handleJoinClick}
                >
                    {isInChannel
                        ? getTranslation("joined") || "Joined"
                        : getTranslation("joinChannel") || "Join channel"}
                </button>
            </div>

            {/* HAKKINDA */}
            {descParagraphs.length > 0 && (
                <section className="mch-section">
                    <h2 className="mch-section-title">
                        {getTranslation("aboutChannel") || "About this channel"}
                    </h2>
                    <div className="mch-section-text">
                        {descParagraphs.map((p, idx) => (
                            <p key={idx}>{p}</p>
                        ))}
                    </div>
                </section>
            )}

            {/* EVENTS */}
            <section className="mch-section">
                <h2 className="mch-section-title">
                    {getTranslation("events") || "Events"}
                </h2>

                {eventsLoading && (
                    <div className="mch-loading">
                        {getTranslation("loading") || "Loading..."}
                    </div>
                )}

                {!eventsLoading && eventsError && (
                    <div className="mch-error">{eventsError}</div>
                )}

                {!eventsLoading && !eventsError && events.length === 0 && (
                    <div className="mch-empty">
                        {getTranslation("noEvents") || "No events for this channel."}
                    </div>
                )}

                {!eventsLoading && !eventsError && events.length > 0 && (
                    <div className="mch-events-list">
                        {events.map((ev) => {
                            const key = ev.eventID || ev.id;
                            const coverSrc = ev.cover || previewPlaceholder;

                            return (
                                <button
                                    key={key}
                                    type="button"
                                    className="mch-event-card"
                                    onClick={() => handleEventCardClick(ev)}
                                >
                                    <div className="mch-event-cover-wrap">
                                        <img
                                            src={coverSrc}
                                            alt={ev.title || "Event cover"}
                                            className="mch-event-cover"
                                            loading="lazy"
                                            onError={(e) => {
                                                if (e.currentTarget.src !== previewPlaceholder) {
                                                    e.currentTarget.src = previewPlaceholder;
                                                }
                                            }}
                                        />
                                    </div>
                                </button>
                            );
                        })}
                    </div>
                )}
            </section>
        </div>
    );
}